import type { SedeConfig } from './types'

/**
 * Helpers for sedi whose legal/contact details are still placeholders
 * (e.g. Verde Valsesia before registration — see verde-valsesia.ts).
 * Components use these to hide rows instead of rendering
 * "+39 000 000 0000" / "P.IVA 00000000000" / "TBD" to visitors.
 *
 * Pure module — no DOM references, safe from Node contexts.
 */

/** True when the phone is missing or the all-zeros placeholder. */
export function isPlaceholderPhone(phone: string | undefined | null): boolean {
  if (!phone) return true
  const digits = phone.replace(/\D/g, '').replace(/^39/, '')
  return digits.length === 0 || /^0+$/.test(digits)
}

/** True when the P.IVA is missing or all zeros. */
export function isPlaceholderPiva(piva: string | undefined | null): boolean {
  if (!piva) return true
  const digits = piva.replace(/\D/g, '')
  return digits.length === 0 || /^0+$/.test(digits)
}

/** True when the founder's name hasn't been filled in yet. */
export function isPlaceholderFounder(name: string | undefined | null): boolean {
  return !name || name.trim().toUpperCase() === 'TBD'
}

/**
 * ContactForm `contactInfo` for a sede. When the founder is still a
 * placeholder, owner AND addressLine are dropped as a PAIR so the
 * shared form falls back to its matched network-owner defaults — the
 * GDPR disclosure must never mix "Andrea Vetri" with a placeholder
 * seat like "Valsesia, VC", nor render "Il Titolare ... è TBD".
 */
export function publicContactInfo(sede: SedeConfig): {
  email: string
  phone: string
  privacyEmail: string
  owner?: string
  addressLine?: string
} {
  const founderIsPlaceholder = isPlaceholderFounder(sede.founder?.name)
  return {
    email: sede.contact.email,
    phone: sede.contact.phone,
    privacyEmail: sede.contact.privacyEmail,
    owner: founderIsPlaceholder ? undefined : sede.founder?.name,
    addressLine: founderIsPlaceholder ? undefined : sede.contact.address,
  }
}

/** True for empty/whitespace, "TBD" (any case) or an all-zero digit string. */
export function isPlaceholderText(value: string | undefined | null): boolean {
  if (!value) return true
  const v = value.trim()
  if (!v || v.toUpperCase() === 'TBD') return true
  const digits = v.replace(/\D/g, '')
  return digits.length > 0 && digits.length === v.replace(/[\s./-]/g, '').length && /^0+$/.test(digits)
}

/** True for empty urls or obvious placeholders (example.*, REPLACE). */
export function isPlaceholderUrl(value: string | undefined | null): boolean {
  if (!value) return true
  const v = value.trim()
  return !v || /example\./i.test(v) || /REPLACE/i.test(v)
}

/** Returns the value unless it is a placeholder; keeps JSON-LD/HTML free of zeros and TBDs. */
export function gated<T>(value: T | undefined | null, isPlaceholder: (v: T) => boolean): T | undefined {
  if (value === undefined || value === null) return undefined
  return isPlaceholder(value) ? undefined : value
}
